import { useAtom } from "@dbeining/react-atom";
import { Form, Formik } from "formik";
import { useState } from "react";
import { Alert, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { userAtom } from "../../atoms";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { EditFormSaveCancelButtons, EditFormSplitFooter } from "../../components/Forms";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import type { LocationCreatePayload } from "../../customTypings/location/Location";
import { useGoogleMapsConfig } from "../../hooks/useGoogleMapsConfig";
import { locationsService } from "../../services/locations/locations.service";
import {
    createEditFormSubmitHandler,
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    optionalNullableString,
    editFormBreadcrumbLabel,
    editPageTitle,
    EntityLabels,
    ListPageTitles,
    hydratePostalAddressForForm,
    loadingPageTitle,
    mapPostalAddressToApi,
    SectionLabels,
    showToastError,
    showToastSuccess,
} from "../../utils";
import { canMutateLocations, formatLocationToastLabel } from "../LocationsListPage/locationAdminListActions";
import { LocationEditFormGrid, type LocationFormValues } from "./LocationEditFormGrid";

const coordinateField = Yup.number()
    .transform((_value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
            return undefined;
        }
        return Number(originalValue);
    })
    .typeError("Please pick a point on the map or search for an address.")
    .required("Please pick a point on the map or search for an address.");

const validationSchema = Yup.object({
    id: Yup.string().optional(),
    name: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Name is required.")
        .max(255, "Name must be at most 255 characters."),
    addressLine1: optionalNullableString,
    addressLine2: optionalNullableString,
    addressLine3: optionalNullableString,
    town: optionalNullableString,
    county: optionalNullableString,
    postcode: optionalNullableString,
    country: optionalNullableString,
    latitude: coordinateField,
    longitude: coordinateField,
    isEnabled: Yup.boolean().required(),
});

const emptyForm: LocationFormValues = {
    name: "",
    ...hydratePostalAddressForForm({}),
    latitude: null,
    longitude: null,
    isEnabled: true,
};

/** Create-only page at `/locations/new`. Edit existing locations via {@link LocationEditLayout}. */
const LocationEditPage = () => {
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateLocations(roleNames);
    const { config: mapConfig, loading: mapConfigLoading, error: mapConfigError } = useGoogleMapsConfig();

    const mapsEnabled =
        mapConfig?.enabled === true && Boolean(mapConfig.browserApiKey) && Boolean(mapConfig.mapId);

    const [initialValues] = useState<LocationFormValues>(emptyForm);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    if (!canMutate) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage locations.</Alert>
            </Container>
        );
    }

    if (mapConfigLoading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.location)} />
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.location, true)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: SectionLabels.operations, to: "/operations" },
                    { text: ListPageTitles.locations, to: "/locations" },
                    { text: editFormBreadcrumbLabel(true) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
            >
                <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                    {editPageTitle(EntityLabels.location, true)}
                </h1>
            </div>

            {mapConfigError && (
                <Alert variant="danger" className="mb-3">
                    {mapConfigError}
                </Alert>
            )}

            {!mapsEnabled && (
                <Alert variant="warning" className="mb-3">
                    Maps are not set up for this system. You cannot use the map or address search until an administrator enables maps.
                    {" "}You cannot create a new location until maps are enabled and coordinates can be set.
                </Alert>
            )}

            <Formik<LocationFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur
                onSubmit={async (values) => {
                    if (!mapsEnabled) {
                        showToastError("Maps must be turned on before you can create a location on the map.");
                        return;
                    }
                    setLiveValidationAfterSubmit(true);
                    try {
                        const addressPayload = mapPostalAddressToApi(values);
                        const createPayload: LocationCreatePayload = {
                            name: values.name.trim(),
                            latitude: values.latitude!,
                            longitude: values.longitude!,
                            isEnabled: values.isEnabled,
                            ...addressPayload,
                        };
                        const created = await locationsService.create(createPayload);
                        setLiveValidationAfterSubmit(false);
                        showToastSuccess(`${formatLocationToastLabel(values.name)} created successfully!`);
                        navigate(`/locations/${created.id}`, { replace: true });
                    } catch {
                        showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ isSubmitting, values, submitCount, handleSubmit, validateForm, setTouched }) => {
                    const createSaveBlocked =
                        !mapsEnabled || values.latitude == null || values.longitude == null;

                    return (
                        <Form
                            noValidate
                            onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
                        >
                            <LocationEditFormGrid
                                mapConfig={mapConfig}
                                mapsEnabled={mapsEnabled}
                                metaDeleted={false}
                                isNew
                                liveValidationAfterSubmit={liveValidationAfterSubmit}
                                submitCount={submitCount}
                            />

                            <EditFormSplitFooter>
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={createSaveBlocked}
                                    saveLabel="Create"
                                    onCancel={() => navigate("/locations")}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );
                }}
            </Formik>
        </Container>
    );
};

export { LocationEditPage };
